import { BooleanInput, Button, Container, Label, NumericInput, TextInput, VectorInput } from '@playcanvas/pcui';

import { Events } from '../events';
import { emptyGuides, formatMeters, formatSquareMeters, measurementValue, readGuides, type Measurement, type Point3, type SpatialGuides } from '../spatial-guides-data';

type MeasureState = { count: number; length: number; hasSelection: boolean; knownLength: number };
type AdvancedState = { count: number; value: number; valid: boolean; direction: 'front' | 'back'; editMode?: 'points' | 'move' | 'rotate'; coordSpace?: 'local' | 'world'; planeLocked?: boolean };
type ExpandSection = () => void;

class MeasurementsPanel extends Container {
    constructor(events: Events) {
        super({ id: 'measurements-panel', class: ['panel', 'options-panel'], hidden: true });
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick', 'keydown', 'keyup'].forEach((name) => {
            this.dom.addEventListener(name, event => event.stopPropagation());
        });
        let suppress = false;
        const panelContent = new Container({ class: 'measurement-panel-content' });
        let parent: Container = panelContent;
        const expandSection = new Map<string, ExpandSection>();
        const status = new Label({ class: 'panel-data-hint', text: '' });
        const data = (): SpatialGuides => events.invokeOptional('spatialGuides') ?? emptyGuides();
        const append = (item: Container | Label | Button) => parent.append(item);
        const row = (text: string) => {
            const container = new Container({ class: 'settings-panel-row' });
            if (text) container.append(new Label({ class: 'settings-panel-row-label', text }));
            append(container);
            return container;
        };
        const visibilityToggle = (label: string, change: (visible: boolean) => void) => {
            const toggle = new BooleanInput({ type: 'toggle', class: ['settings-panel-row-toggle', 'section-visibility-toggle'], value: true });
            toggle.dom.setAttribute('aria-label', label);
            toggle.dom.addEventListener('click', event => event.stopPropagation());
            toggle.dom.addEventListener('keydown', event => event.stopPropagation());
            toggle.on('change', (visible: boolean) => {
                if (!suppress) {
                    events.fire('measurement.deselect');
                    change(visible);
                }
            });
            return toggle;
        };
        const section = (text: string, expanded: boolean, toggle?: BooleanInput, nested = false) => {
            const header = new Container({ class: ['panel-header', 'options-panel-section', 'measurement-section-header', ...(nested ? ['measurement-subsection-header'] : [])] });
            const arrow = new Label({ class: 'measurement-collapse-arrow' });
            arrow.dom.setAttribute('aria-hidden', 'true');
            arrow.dom.setAttribute('data-expanded', String(expanded));
            header.append(new Label({ class: 'panel-header-label', text }));
            if (toggle) header.append(toggle);
            header.append(arrow);
            const body = new Container({ class: ['measurement-section-body', ...(nested ? ['measurement-subsection-body'] : [])], hidden: !expanded });
            parent.append(header);
            parent.append(body);
            header.dom.setAttribute('role', 'button');
            header.dom.setAttribute('tabindex', '0');
            header.dom.setAttribute('aria-expanded', String(expanded));
            const toggleBody = () => {
                body.hidden = !body.hidden;
                arrow.dom.setAttribute('data-expanded', String(!body.hidden));
                header.dom.setAttribute('aria-expanded', String(!body.hidden));
            };
            expandSection.set(text, () => {
                if (body.hidden) toggleBody();
            });
            header.on('click', toggleBody);
            header.dom.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleBody();
                }
            });
            return body;
        };
        const action = (container: Container, text: string, event: string) => {
            const button = new Button({ text });
            button.on('click', () => events.fire(event));
            container.append(button);
            return button;
        };
        const place = (container: Container, text: string, tool: string) => {
            const button = new Button({ text });
            const updateActive = () => button.dom.setAttribute('aria-pressed', String(events.invokeOptional('tool.active') === tool));
            events.on('tool.activated', updateActive);
            updateActive();
            button.on('click', () => {
                if (events.invokeOptional('tool.active') === tool) {
                    events.fire('measurement.cancel');
                } else {
                    events.fire('measurement.cancel');
                    events.fire(`tool.${tool}`);
                }
            });
            container.append(button);
            return button;
        };
        const update = (next: SpatialGuides) => {
            try {
                events.fire('spatialGuides.update', readGuides(next));
                status.text = '';
            } catch (error) {
                status.text = String(error);
            }
        };

        const title = new Container({ class: 'panel-header' });
        title.append(new Label({ class: 'panel-header-label', text: 'Scene References' }));
        const close = new Button({ text: '×' });
        close.dom.setAttribute('aria-label', 'Close Scene References');
        close.on('click', () => events.fire('measurementsPanel.setVisible', false));
        title.append(close);
        this.append(title);
        this.append(panelContent);

        const referenceVisible = visibilityToggle('Show scale reference', visible => update({ ...data(), referenceVisible: visible }));
        parent = section('Scale reference', true, referenceVisible);
        const reference = new Label({ class: 'panel-data-hint', text: 'Place two reference points, then set their known distance.' });
        append(reference);
        const referenceActions = row('');
        place(referenceActions, 'Place reference', 'distanceScale');
        const clearReference = action(referenceActions, 'Clear points', 'distanceScale.clear');
        const knownLength = new NumericInput({ class: 'settings-panel-row-number', value: 1, precision: 4, min: 0.0001 });
        row('Known distance (m)').append(knownLength);
        knownLength.on('change', (value: number) => {
            if (!suppress) events.fire('distanceScale.length', value);
        });
        const apply = action(row(''), 'Scale scene', 'distanceScale.apply');
        apply.enabled = false;
        const removeReference = new Button({ text: 'Remove reference' });
        row('').append(removeReference);
        removeReference.on('click', () => {
            update({ ...data(), measurements: data().measurements.filter(m => m.id !== 'scale-reference') });
            events.fire('distanceScale.clear');
        });

        parent = panelContent;
        panelContent.append(new Container({ class: 'measurement-section-separator' }));
        const show = visibilityToggle('Show all measurements', visible => update({ ...data(), measurementsVisible: visible }));
        const measurementsBody = section('Measurements', false, show);
        parent = measurementsBody;
        const showOnLoad = new BooleanInput({ type: 'toggle', class: 'settings-panel-row-toggle', value: false });
        row('Show in viewer on load').append(showOnLoad);
        showOnLoad.on('change', (visible: boolean) => {
            if (!suppress) update({ ...data(), showOnLoad: visible });
        });

        const distanceVisible = visibilityToggle('Show distance measurements', visible => update({ ...data(), distanceVisible: visible }));
        parent = measurementsBody;
        parent = section('Distance', false, distanceVisible, true);
        const measureActions = row('');
        place(measureActions, 'Place measurement', 'measure');
        const clear = action(measureActions, 'Clear points', 'measure.clear');
        const currentLength = new Label({ class: 'panel-data-hint', text: 'Place two points to measure. Select a point to reposition it.' });
        append(currentLength);
        const name = new TextInput({ class: 'settings-panel-row-select', placeholder: 'Measurement name' });
        row('Name').append(name);
        const save = new Button({ text: 'Save measurement', enabled: false });
        save.on('click', () => events.fire('measure.save', name.value.trim()));
        row('').append(save);

        const areaVisible = visibilityToggle('Show area measurements', visible => update({ ...data(), areaVisible: visible }));
        parent = measurementsBody;
        parent = section('Area', false, areaVisible, true);
        const areaActions = row('');
        place(areaActions, 'Place polygon points', 'areaMeasure');
        const clearArea = action(areaActions, 'Clear points', 'areaMeasure.clear');
        const areaModes = row('');
        const areaModeButtons = (['points', 'move', 'rotate'] as const).map((mode, index) => {
            const button = new Button({ text: ['Points', 'Move area', 'Rotate area'][index] });
            button.on('click', () => events.fire('areaMeasure.editMode', mode));
            areaModes.append(button);
            return button;
        });
        const areaModeHint = new Label({ class: 'panel-data-hint', text: '' });
        append(areaModeHint);
        const coordinateControls = (mode: string) => {
            const controls = row('Axes');
            return (['local', 'world'] as const).map((space) => {
                const button = new Button({ text: space === 'local' ? 'Local' : 'Global' });
                button.dom.setAttribute('aria-label', `${mode === 'areaMeasure' ? 'Area' : 'Depth'} ${space === 'local' ? 'local' : 'global'} axes`);
                button.on('click', () => events.fire(`${mode}.coordSpace`, space));
                controls.append(button);
                return button;
            });
        };
        const areaCoordinates = coordinateControls('areaMeasure');
        const currentArea = new Label({ class: 'panel-data-hint', text: 'Place at least three polygon points.' });
        append(currentArea);
        const flattenArea = action(row(''), 'Flatten area', 'areaMeasure.flatten');
        flattenArea.dom.setAttribute('title', 'Project all polygon points onto a plane through their average center, keeping the polygon orientation.');
        const areaName = new TextInput({ class: 'settings-panel-row-select', placeholder: 'Area name' });
        row('Name').append(areaName);
        const saveArea = new Button({ text: 'Save area', enabled: false });
        saveArea.on('click', () => events.fire('areaMeasure.save', areaName.value.trim()));
        row('').append(saveArea);

        const depthVisible = visibilityToggle('Show depth measurements', visible => update({ ...data(), depthVisible: visible }));
        parent = measurementsBody;
        parent = section('Depth', false, depthVisible, true);
        const depthActions = row('');
        place(depthActions, 'Place plane and point', 'depthMeasure');
        const clearDepth = action(depthActions, 'Clear points', 'depthMeasure.clear');
        const depthCoordinates = coordinateControls('depthMeasure');
        const depthAxesHint = new Label({ class: 'panel-data-hint', text: '' });
        append(depthAxesHint);
        const currentDepth = new Label({ class: 'panel-data-hint', text: 'Place three plane points, then a depth point.' });
        append(currentDepth);
        const directionActions = row('Direction');
        const front = new Button({ text: 'Front' });
        const back = new Button({ text: 'Back' });
        front.on('click', () => events.fire('depthMeasure.direction', 'front'));
        back.on('click', () => events.fire('depthMeasure.direction', 'back'));
        directionActions.append(front);
        directionActions.append(back);
        const depthName = new TextInput({ class: 'settings-panel-row-select', placeholder: 'Depth name' });
        row('Name').append(depthName);
        const saveDepth = new Button({ text: 'Save depth', enabled: false });
        saveDepth.on('click', () => events.fire('depthMeasure.save', depthName.value.trim()));
        row('').append(saveDepth);

        parent = measurementsBody;
        const measurements = new Container({ class: 'measurements-list' });
        append(measurements);
        const cancelEdit = new Button({ text: 'Cancel editing', hidden: true });
        cancelEdit.on('click', () => events.fire('measurement.cancel'));
        append(cancelEdit);

        parent = panelContent;
        panelContent.append(new Container({ class: 'measurement-section-separator' }));
        const showBox = visibilityToggle('Show bounding box', (visible) => {
            if (data().boundingBox) update({ ...data(), boundingBox: { ...data().boundingBox, visible } });
        });
        parent = section('Bounding box', false, showBox);
        action(row(''), 'Fit box to scene', 'spatialGuides.fitBox');
        const vectors = ['Center (m)', 'Size (m)', 'Rotation (°)'].map((label, index) => {
            const input = new VectorInput({ class: 'settings-panel-row-vector', dimensions: 3, precision: 4, placeholder: ['X', 'Y', 'Z'], value: index === 1 ? [1, 1, 1] : [0, 0, 0] });
            row(label).append(input);
            return input;
        });
        const boxActions = row('');
        const saveBox = new Button({ text: 'Save box' });
        saveBox.on('click', () => update({ ...data(),
            boundingBox: {
                center: vectors[0].value as Point3,
                size: vectors[1].value as Point3,
                rotation: vectors[2].value as Point3,
                visible: true
            } }));
        const removeBox = new Button({ text: 'Remove box' });
        removeBox.on('click', () => update({ ...data(), boundingBox: null }));
        boxActions.append(saveBox);
        boxActions.append(removeBox);

        parent = panelContent;
        append(status);
        const files = row('');
        const download = new Button({ text: 'Export References…' });
        download.on('click', () => {
            const url = URL.createObjectURL(new Blob([JSON.stringify(data(), null, 2)], { type: 'application/json' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'scene-references.json';
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        });
        const file = document.createElement('input');
        file.type = 'file';
        file.accept = '.json,application/json';
        file.hidden = true;
        file.addEventListener('change', async () => {
            try {
                if (file.files?.[0]) update(readGuides(JSON.parse(await file.files[0].text())));
            } catch (error) {
                status.text = String(error);
            }
            file.value = '';
        });
        this.dom.append(file);
        const upload = new Button({ text: 'Import References…' });
        upload.on('click', () => file.click());
        files.append(download);
        files.append(upload);
        const sceneFiles = row('');
        sceneFiles.dom.setAttribute('style', 'flex-wrap: wrap');
        const exportScene = new Button({ text: 'Export JSI Scene Data…' });
        exportScene.on('click', async () => {
            try {
                const value = await events.invoke('sceneData.export');
                const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }));
                const link = document.createElement('a');
                link.href = url;
                link.download = 'jsi-scene-data.json';
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                status.text = 'Scene data exported. Attribution values are stored in SOG/project exports, not this editor companion.';
            } catch (error) {
                status.text = String(error);
            }
        });
        const sceneFile = document.createElement('input');
        sceneFile.type = 'file';
        sceneFile.accept = '.json,application/json';
        sceneFile.hidden = true;
        sceneFile.addEventListener('change', async () => {
            try {
                const selected = sceneFile.files?.[0];
                if (selected) {
                    if (selected.size > 256000000) throw new Error('Scene data exceeds 256 MB.');
                    if (await events.invoke('sceneData.import', JSON.parse(await selected.text()))) status.text = 'JSI Scene Data imported. Undo restores the previous layers.';
                }
            } catch (error) {
                status.text = String(error);
            }
            sceneFile.value = '';
        });
        this.dom.append(sceneFile);
        const importScene = new Button({ text: 'Import JSI Scene Data…' });
        importScene.on('click', () => sceneFile.click());
        sceneFiles.append(exportScene);
        sceneFiles.append(importScene);

        const refresh = () => {
            suppress = true;
            const guides = data();
            const savedReference = guides.measurements.find(m => m.id === 'scale-reference');
            const scaleState: MeasureState = events.invokeOptional('distanceScale.state');
            const measureState: MeasureState = events.invokeOptional('measure.state');
            const areaState: AdvancedState = events.invokeOptional('areaMeasure.state');
            const depthState: AdvancedState = events.invokeOptional('depthMeasure.state');
            [areaCoordinates, depthCoordinates].forEach((buttons, index) => {
                const state = index === 0 ? areaState : depthState;
                buttons.forEach((button, i) => {
                    button.dom.setAttribute('aria-pressed', String((state?.planeLocked ? 'local' : state?.coordSpace ?? 'local') === (i === 0 ? 'local' : 'world')));
                    button.enabled = !state?.planeLocked;
                });
            });
            depthAxesHint.text = depthState?.planeLocked ? 'Root and endpoint are locked to plane-local axes.' : 'Axes apply to plane points; root and endpoint stay plane-constrained.';
            const active = events.invokeOptional('tool.active');
            const editing: Measurement = events.invokeOptional('measurement.editing');
            cancelEdit.hidden = !editing;
            save.text = editing && !['area', 'depth'].includes(editing.type) ? 'Save changes' : 'Save measurement';
            saveArea.text = editing?.type === 'area' ? 'Save changes' : 'Save area';
            saveDepth.text = editing?.type === 'depth' ? 'Save changes' : 'Save depth';
            reference.text = scaleState?.count && active === 'distanceScale' ? `Reference: ${scaleState.count === 2 ? formatMeters(scaleState.length) : 'place the second point'}` :
                savedReference ? `Saved reference: ${measurementValue(savedReference)}` : 'Place two reference points, then set their known distance.';
            knownLength.value = scaleState?.knownLength ?? 1;
            apply.enabled = active === 'distanceScale' && scaleState?.count === 2 && scaleState.length > 1e-8 && Number.isFinite(knownLength.value) && knownLength.value > 0;
            clearReference.enabled = (scaleState?.count ?? 0) > 0;
            removeReference.enabled = !!savedReference;
            currentLength.text = measureState?.count === 2 ? `Distance: ${formatMeters(measureState.length)}` : 'Place two points to measure. Select a point to reposition it.';
            clear.enabled = (measureState?.count ?? 0) > 0;
            save.enabled = active === 'measure' && measureState?.count === 2;
            currentArea.text = areaState?.count >= 3 ? `Area: ${formatSquareMeters(areaState.value)}` : `Place polygon points (${areaState?.count ?? 0}/3 minimum).`;
            clearArea.enabled = (areaState?.count ?? 0) > 0;
            saveArea.enabled = active === 'areaMeasure' && !!areaState?.valid;
            flattenArea.enabled = active === 'areaMeasure' && !!areaState?.valid;
            areaModeButtons.forEach((button, index) => {
                button.enabled = active === 'areaMeasure' && (index === 0 || areaState?.count >= 3);
                button.dom.setAttribute('aria-pressed', String((areaState?.editMode ?? 'points') === ['points', 'move', 'rotate'][index]));
            });
            areaModeHint.text = areaState?.editMode === 'move' ? 'Move all points with the center gizmo. Point placement is paused.' :
                areaState?.editMode === 'rotate' ? 'Rotate all points around their center. Point placement is paused.' : 'Place points or select a vertex to move it.';
            currentDepth.text = depthState?.count === 4 ? `Depth: ${formatMeters(depthState.value)}` : `Place three plane points, then a depth point (${depthState?.count ?? 0}/4).`;
            clearDepth.enabled = (depthState?.count ?? 0) > 0;
            saveDepth.enabled = active === 'depthMeasure' && !!depthState?.valid;
            front.dom.setAttribute('aria-pressed', String(depthState?.direction !== 'back'));
            back.dom.setAttribute('aria-pressed', String(depthState?.direction === 'back'));
            referenceVisible.value = guides.referenceVisible ?? true;
            show.value = guides.measurementsVisible;
            distanceVisible.value = guides.distanceVisible ?? true;
            areaVisible.value = guides.areaVisible ?? true;
            depthVisible.value = guides.depthVisible ?? true;
            showOnLoad.value = guides.showOnLoad ?? false;
            showBox.value = guides.boundingBox?.visible ?? false;
            showBox.enabled = !!guides.boundingBox;
            removeBox.enabled = !!guides.boundingBox;
            measurements.clear();
            guides.measurements.filter(m => m.id !== 'scale-reference').forEach((measurement) => {
                const item = new Container({ class: 'measurement-item' });
                const kind = measurement.type === 'area' ? 'Area' : measurement.type === 'depth' ? 'Depth' : 'Distance';
                item.append(new Label({ text: `${kind} · ${measurement.name}: ${measurementValue(measurement)}` }));
                const edit = new Button({ text: 'Edit' });
                edit.dom.setAttribute('aria-label', `Edit ${measurement.name}`);
                edit.on('click', () => events.fire('measurement.edit', measurement.id));
                item.append(edit);
                const remove = new Button({ text: '×' });
                remove.dom.setAttribute('aria-label', `Remove ${measurement.name}`);
                remove.on('click', () => update({ ...data(), measurements: data().measurements.filter(m => m.id !== measurement.id) }));
                item.append(remove);
                measurements.append(item);
            });
            suppress = false;
        };
        events.on('spatialGuides.changed', () => {
            const box = data().boundingBox;
            if (box) {
                [box.center, box.size, box.rotation].forEach((value, index) => {
                    vectors[index].value = value;
                });
            }
            refresh();
        });
        events.on('measurement.error', (message: string) => {
            status.text = message;
        });
        events.on('measurement.editing', (measurement: Measurement | null) => {
            if (measurement) {
                const kind = measurement.type === 'area' ? 'Area' : measurement.type === 'depth' ? 'Depth' : 'Distance';
                const input = kind === 'Area' ? areaName : kind === 'Depth' ? depthName : name;
                input.value = measurement.name;
                expandSection.get('Measurements')();
                expandSection.get(kind)();
                status.text = 'Select points in the scene and move them with the gizmo, then Save changes.';
            } else status.text = '';
            refresh();
        });
        ['measure', 'areaMeasure', 'depthMeasure', 'distanceScale'].forEach(mode => events.on(`${mode}.state`, refresh));
        const others = ['settingsPanel', 'appearancePanel', 'overlaysPanel', 'cameraSettingsPanel', 'attributionMapsPanel'];
        const setVisible = (visible: boolean) => {
            if (visible) others.forEach(panel => events.fire(`${panel}.setVisible`, false));
            this.hidden = !visible;
            if (!visible && ['measure', 'areaMeasure', 'depthMeasure', 'distanceScale'].includes(events.invokeOptional('tool.active'))) {
                events.fire('tool.deactivate');
            }
            if (visible) refresh();
            events.fire('measurementsPanel.visible', visible);
        };
        events.function('measurementsPanel.visible', () => !this.hidden);
        events.on('measurementsPanel.setVisible', setVisible);
        events.on('measurementsPanel.toggleVisible', () => {
            if (!this.hidden) {
                setVisible(false);
            } else {
                setVisible(true);
            }
        });
        others.forEach(panel => events.on(`${panel}.visible`, (visible: boolean) => {
            if (visible && !this.hidden) setVisible(false);
        }));
        events.on('tool.activated', (tool: string) => {
            if (['measure', 'areaMeasure', 'depthMeasure', 'distanceScale'].includes(tool)) setVisible(true);
            else refresh();
        });
        refresh();
    }
}

export { MeasurementsPanel };
